import type { ToolUIPart } from "ai";
import { createContext, useContext, useState, type ReactNode, useCallback, useMemo } from "react";
import { useSubMenu } from "../SubMenuContext";
import { EmpreenderApps } from "../lib/AppsArray";
import { useTranslation } from "../lib/i18n/TranslationContext";

export type Versions = {
    id: string;
    content: string | ReactNode;
    isCompleted: boolean;
}

export type MessageType = {
    key: string;
    from: "user" | "assistant" | "system";
    sources?: { link: string; nomeartigo: string }[];
    id_resposta: number;
    versions: Versions[];
    reasoning?: {
        content: string;
        duration: number;
    };
    tools?: {
        name: string;
        description: string;
        status: ToolUIPart["state"];
        parameters: Record<string, unknown>;
        result: string | undefined;
        error: string | undefined;
    }[];
}

type ChatContextType = {
    isChatOpen: boolean;
    isFullScreen: boolean;
    isDashOpen: boolean;
    toggleChat: (toggle: boolean) => void;
    toggleFullScreen: () => void;
    toggleDash: (toggle: boolean) => void;
    messages: MessageType[];
    setMessages: React.Dispatch<React.SetStateAction<MessageType[]>>;
    addUserMessage: (content: string) => void;
    isStreaming?: boolean;
    handleSubmit: (text: string) => Promise<unknown>;
    isChatReasizing: boolean;
    sendSystemMsg: (msg: string | ReactNode) => void;
    setWhatsappLink: (link: string) => void;
    whatsappLink: string;
    enderecoDaCentral: string;
};

const CENTRAL_DE_AJUDA = 'https://www.suporte.com.br/embed';

type ChatProviderProps = {
    children: ReactNode;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: ChatProviderProps) {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isDashOpen, setIsDashOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isChatReasizing, setIsChatReasoning] = useState(false);
    const [whatsappLink, setWhatsappLink] = useState('');

    const [messages, setMessages] = useState<MessageType[]>([]);

    const { app } = useSubMenu();
    const { lingua } = useTranslation();

    const matchApp = EmpreenderApps.find((a) => a.tags.some((tag) => tag?.toLowerCase() === app?.toLowerCase()));

    const enderecoDaCentral = useMemo(() => {
        const slug = matchApp?.slug ?? 'plus';
        return `${CENTRAL_DE_AJUDA}?app=${encodeURIComponent(slug)}&lang=${lingua === 'es-mx' ? 'es' : 'pt'}`;
    }, [matchApp?.slug, lingua]);

    const isStreaming = useMemo(() => {
        return (
            isChatReasizing ||
            messages.some(msg =>
                msg.versions.some(v => !v.isCompleted)
            )
        );
    }, [isChatReasizing, messages]);

    const toggleChat = (toggle: boolean) => {
        setIsChatOpen(toggle);
        setIsFullScreen(false);
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const toggleDash = (toggle: boolean) => {
        setIsDashOpen(toggle);
        setIsFullScreen(false);
    }

    const streamResponse = useCallback(
        async (messageId: string, content: string | ReactNode) => {

            if (typeof content !== "string") {
                setMessages(prev =>
                    prev.map(msg => ({
                        ...msg,
                        versions: msg.versions.map(v =>
                            v.id === messageId
                                ? { ...v, content, isCompleted: true }
                                : v
                        ),
                    }))
                );
                return;
            }

            const words = content.split(" ");
            let currentContent = "";

            setMessages(prev =>
                prev.map(msg => ({
                    ...msg,
                    versions: msg.versions.map(v =>
                        v.id === messageId
                            ? { ...v, content: "", isCompleted: false }
                            : v
                    ),
                }))
            );

            for (let i = 0; i < words.length; i++) {
                currentContent += (i > 0 ? " " : "") + words[i];

                setMessages(prev =>
                    prev.map(msg => ({
                        ...msg,
                        versions: msg.versions.map(v =>
                            v.id === messageId
                                ? { ...v, content: currentContent }
                                : v
                        ),
                    }))
                );

                await new Promise(r =>
                    setTimeout(r, Math.random() * 100 + 50)
                );
            }

            setMessages(prev =>
                prev.map(msg => ({
                    ...msg,
                    versions: msg.versions.map(v =>
                        v.id === messageId
                            ? { ...v, isCompleted: true }
                            : v
                    ),
                }))
            );
        },
        []
    );

    const addUserMessage = useCallback(
        (content: string) => {
            const userMessage: MessageType = {
                key: `user-${Date.now()}`,
                from: "user",
                id_resposta: Date.now(),
                versions: [
                    {
                        id: `user-${Date.now()}`,
                        content,
                        isCompleted: true,
                    },
                ],
            };

            setMessages((prev) => [...prev, userMessage]);
        },
        []
    );

    const handleSubmit = async (text: string) => {
        if (!text || isStreaming) return;
        setIsChatReasoning(true);
        addUserMessage(text);

        const data = {
            pergunta: text,
            categorias: [],
            num_artigos: 5,
            origem: "app-lib-empreender",
            idioma: 'pt-br',
        };

        const controller = new AbortController();

        const response = await fetch(
            `https://app.sak.com.br/Bot/conversar/${matchApp?.slug}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
                signal: controller.signal,
            }
        );
        const res = await response.json();


        const { resposta, artigos } = res;
        let { id_resposta } = res;

        if (!id_resposta) id_resposta = Date.now();

        const assistantMessageId = `assistant-${id_resposta}`;

        const assistantMessage: MessageType = {
            key: `assistant-${Date.now()}`,
            from: "assistant",
            sources: artigos,
            id_resposta: id_resposta,
            versions: [
                {
                    id: assistantMessageId,
                    content: resposta,
                    isCompleted: false,
                },
            ],
        };

        setMessages((prev) => [...prev, assistantMessage]);
        streamResponse(assistantMessageId, resposta);

        setIsChatReasoning(false);

        return res;
    }

    const sendSystemMsg = (msg: string | ReactNode) => {
        const id = Date.now();
        const assistantMessageId = `system-${id}`;
        setIsChatReasoning(true);

        setTimeout(() => {
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    key: `system-${id}`,
                    from: "system",
                    id_resposta: id,
                    versions: [
                        {
                            id: `system-${id}`,
                            content: msg,
                            isCompleted: true,
                        },
                    ],
                }
            ]);
            streamResponse(assistantMessageId, msg);
            setIsChatReasoning(false);
        }, 700);

    };

    return (
        <ChatContext.Provider value={{ isStreaming, messages, setMessages, isChatOpen, toggleChat, toggleFullScreen, isFullScreen, toggleDash, isDashOpen, addUserMessage, handleSubmit, isChatReasizing, sendSystemMsg, whatsappLink, setWhatsappLink, enderecoDaCentral }}>
            {children}
        </ChatContext.Provider>
    );
}


export function useChat() {
    const context = useContext(ChatContext);

    if (!context) {
        throw new Error("useChat must be used within ChatProvider");
    }

    return context;
}